import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { copyProtectionProps } from "./copy-protection";
import { TaskFocusModeContext } from "./task-focus-mode";
import { useTaskFocusController } from "./use-task-focus-controller";

/**
 * Frame of a contribution task page.
 *
 * ```tsx
 * <TaskShell header={<TaskHeader task="speak" ... />} notices={<ValidationGateBanner />} actionBar={<TaskActionBar ... />}>
 *   <TaskItemCard ...>...</TaskItemCard>
 * </TaskShell>
 * ```
 *
 * - `header`: the compact TaskHeader.
 * - `notices`: banners above the item (validation gate, re-record reason...).
 * - `children`: the item to handle, usually one TaskItemCard and its side panel.
 * - `actionBar`: a TaskActionBar. It sticks to the bottom of the screen (above
 *   the phone bottom nav) and rests under the content at the end of the page,
 *   so it never hides the last lines.
 * - `width`: "narrow" (one column) or "wide" (room for a side panel). Focus
 *   mode uses a larger cap for each layout while remaining viewport-bound.
 * - Focus mode can use browser full screen or a fixed viewport fallback.
 *
 * The page content cannot be selected, copied or dragged out (fields excepted):
 * the rest of the site stays selectable.
 */
export function TaskShell({
  header,
  notices,
  actionBar,
  children,
  width = "narrow",
  className,
}: {
  header: ReactNode;
  notices?: ReactNode;
  actionBar?: ReactNode;
  children: ReactNode;
  width?: "narrow" | "wide";
  className?: string;
}) {
  const { shellRef, controller } = useTaskFocusController();
  const { isFocused } = controller;
  const max = isFocused
    ? width === "wide"
      ? "max-w-[112rem]"
      : "max-w-[90rem]"
    : width === "wide"
      ? "max-w-5xl"
      : "max-w-3xl";

  return (
    <TaskFocusModeContext.Provider value={controller}>
      <div
        ref={shellRef}
        data-focus-mode={isFocused ? "active" : undefined}
        className={cn(
          "copy-protected flex flex-col bg-background",
          isFocused ? "fixed inset-0 z-[100] h-dvh min-h-dvh overflow-x-clip overflow-y-auto" : "min-h-[calc(100dvh-4.5rem)]",
          className,
        )}
        {...copyProtectionProps}
      >
        <div className={cn("mx-auto w-full min-w-0 flex-1 space-y-5 px-4 pb-8 pt-5 sm:px-6 sm:pt-6", max)}>
          {header}
          {notices ? <div className="min-w-0 space-y-3">{notices}</div> : null}
          {children}
        </div>
        {actionBar ? (
          <div className={cn("sticky z-30 mt-auto border-t border-border/60 bg-background", isFocused ? "bottom-0" : "bottom-[calc(env(safe-area-inset-bottom)_+_5.75rem)] md:bottom-0")}>
            <div className={cn("mx-auto w-full min-w-0 px-4 py-3 sm:px-6", max)}>{actionBar}</div>
          </div>
        ) : null}
      </div>
    </TaskFocusModeContext.Provider>
  );
}
